/**
 * Coalesce bursty refetch triggers (SSE reconnect storms, cross-tab sync) so one
 * in-flight run plus at most one trailing run occur within minIntervalMs.
 */
export type CoalescedAction = {
  trigger: () => void;
  dispose: () => void;
};

export function createCoalescedAction(options: {
  minIntervalMs: number;
  run: () => void | Promise<void>;
  setTimer?: (callback: () => void, delayMs: number) => number;
  clearTimer?: (timer: number) => void;
  nowMs?: () => number;
}): CoalescedAction {
  const setTimer = options.setTimer ?? ((callback, delayMs) => window.setTimeout(callback, delayMs));
  const clearTimer = options.clearTimer ?? ((timer) => window.clearTimeout(timer));
  const nowMs = options.nowMs ?? (() => Date.now());
  const minIntervalMs = Math.max(0, options.minIntervalMs);

  let disposed = false;
  let running = false;
  let trailing = false;
  let timer: number | undefined;
  let lastStartedAtMs = 0;

  const clearScheduled = (): void => {
    if (timer != null) {
      clearTimer(timer);
      timer = undefined;
    }
  };

  const afterRun = (): void => {
    running = false;
    if (disposed) {
      return;
    }
    if (!trailing) {
      return;
    }
    trailing = false;
    const elapsed = nowMs() - lastStartedAtMs;
    const waitMs = Math.max(0, minIntervalMs - elapsed);
    if (waitMs === 0) {
      startRun();
      return;
    }
    clearScheduled();
    timer = setTimer(() => {
      timer = undefined;
      startRun();
    }, waitMs);
  };

  const startRun = (): void => {
    if (disposed || running) {
      return;
    }
    running = true;
    lastStartedAtMs = nowMs();
    try {
      const result = options.run();
      if (result != null && typeof (result as Promise<void>).then === "function") {
        void (result as Promise<void>).then(afterRun, afterRun);
        return;
      }
      afterRun();
    } catch {
      afterRun();
    }
  };

  return {
    trigger: () => {
      if (disposed) {
        return;
      }
      if (running || timer != null) {
        trailing = true;
        return;
      }
      const elapsed = nowMs() - lastStartedAtMs;
      if (lastStartedAtMs > 0 && elapsed < minIntervalMs) {
        trailing = true;
        timer = setTimer(() => {
          timer = undefined;
          startRun();
        }, minIntervalMs - elapsed);
        return;
      }
      startRun();
    },
    dispose: () => {
      disposed = true;
      trailing = false;
      clearScheduled();
    },
  };
}
